import React, { ReactElement, memo, useCallback, useMemo } from "react";

// types
import { AddressInfo, UserAddress } from "@swiggy-private/cart-types";

// components
import { format, getAddressInfo } from "@swiggy-private/core-ui";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as WarningFilledIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/warningFilled/20";
import {
    AddressAnnotation,
    AddressDescription,
    AddressLocationDescription,
    IconWrapper,
    SelectedLabel,
    AddressContentContainer,
    AddressContentWrapper,
    AwayAddressWarning,
    AddressTitleContainer,
} from "./styles";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

//test-ids
import { SELECT_ADDRESS_TEST_IDS } from "@swiggy-private/cart-test-ids";

//helpers
import { getAddressAnnotation, getDeliveryIconV2, hasInvalidAddressCategory } from "@cart-ui/helpers/CartFooter";

//constants
const AWAY_ADDRESS_WARNING_TEXT = "{0} away from your current location";
const ACCESSIBILITY_TEXTS = {
    SLA: "Estimated time to delivery at this address is",
    CLICK_TO_SELECT: "Click to select the address",
};
const SELECTED_ADDRESS_LABEL_TEXT = "SELECTED";
export interface AddressProps {
    address: UserAddress | AddressInfo;
    addressList: AddressInfo[];
    selectAddress: (selectedAddress: AddressInfo, addressList: AddressInfo[]) => void;
    showAddressNudgeIcon: boolean;
}

function Icon({
    category,
    isSelected,
    showAddressNudge,
}: {
    category?: number;
    isSelected: boolean;
    showAddressNudge: boolean;
}) {
    const IconElement = getDeliveryIconV2(isSelected, showAddressNudge, category);

    return (
        <GenericIcon
            Icon={IconElement}
            strokeColor={showAddressNudge && isSelected ? "text_inverse_Highest_Emphasis" : "text_Highest_Emphasis"}
            fillColor="text_Highest_Emphasis"
            data-testid={`${SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS_ICON}_${category}`}
        />
    );
}

export const Address = memo(function Address(props: AddressProps): ReactElement {
    const { address, addressList, selectAddress } = props;

    const onAddressSelect = useCallback(() => {
        selectAddress(address as AddressInfo, addressList);
    }, [address, addressList, selectAddress]);

    const addressInfo = useMemo(() => getAddressInfo(address), [address]);

    const addressCategory = useMemo(() => {
        if (hasInvalidAddressCategory(address as AddressInfo)) {
            return (address as AddressInfo).addressCategory;
        }
        return (address as AddressInfo)?.userIntentAddressCategory;
    }, [address]);

    const addressAnnotation = useMemo(() => {
        if (hasInvalidAddressCategory(address as AddressInfo)) {
            return getAddressAnnotation(address, addressCategory);
        }
        return address?.annotation || getAddressAnnotation(address, addressCategory);
    }, [address, addressCategory]);

    const accessibilityText = useMemo(() => {
        let text = `${addressAnnotation} ${addressInfo}`;

        // TODO: remove after address selector XP is concluded
        if (address?.sla_message) {
            text += ` ${ACCESSIBILITY_TEXTS.SLA} ${address.sla_message}`;
        }

        text += ` ${ACCESSIBILITY_TEXTS.CLICK_TO_SELECT}`;
        return text;
    }, [address, addressInfo, addressAnnotation]);

    const { distanceLabel, renderDistanceFromCurrentLocation } = useMemo((): {
        distanceLabel?: string;
        renderDistanceFromCurrentLocation: JSX.Element | null;
    } => {
        const distance = address?.haversineDistance;
        if (!distance) return { renderDistanceFromCurrentLocation: null };
        let distanceLabel: string;
        if (distance < 1000) distanceLabel = `${Math.trunc(distance)} m`;
        else distanceLabel = `${(distance / 1000).toFixed(1)} km`;
        return {
            distanceLabel,
            renderDistanceFromCurrentLocation: (
                <Text
                    variant="V2.caption.neutral.medium"
                    color="text_Highest_Emphasis"
                    data-testid={`${SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS_DISTANCE}_${address.id}`}
                >
                    {distanceLabel}
                </Text>
            ),
        };
    }, [address?.haversineDistance, address.id]);

    return (
        <AddressContentWrapper
            key={address.id}
            onClick={onAddressSelect}
            aria-label={accessibilityText}
            role="button"
            data-testid={`${SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS}_${address.id}`}
        >
            <AddressContentContainer>
                <IconWrapper>
                    <Icon
                        category={addressCategory}
                        isSelected={!!address?.isSelected}
                        showAddressNudge={!!address?.haversineDistance && props.showAddressNudgeIcon}
                    />
                    {renderDistanceFromCurrentLocation}
                </IconWrapper>
                <AddressDescription as="div">
                    <AddressTitleContainer>
                        <AddressAnnotation
                            variant="V2.body2.neutral.bold"
                            color="text_Highest_Emphasis"
                            data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS_ANNOTATION}
                        >
                            {addressAnnotation}
                        </AddressAnnotation>
                        {address?.isSelected && (
                            <SelectedLabel variant="V2.caption.neutral.bold" color="positive">
                                {SELECTED_ADDRESS_LABEL_TEXT}
                            </SelectedLabel>
                        )}
                    </AddressTitleContainer>
                    <AddressLocationDescription
                        variant="V2.body3.neutral.regular"
                        color="text_Med_Emphasis"
                        data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS_LOCATION}
                    >
                        {addressInfo}
                    </AddressLocationDescription>
                </AddressDescription>
            </AddressContentContainer>
            {props?.showAddressNudgeIcon && address.isSelected && distanceLabel && (
                <AwayAddressWarning variant="V2.body3.neutral.medium" color="warning">
                    <GenericIcon Icon={WarningFilledIcon} fillColor="warning" />
                    {format(AWAY_ADDRESS_WARNING_TEXT, distanceLabel)}
                </AwayAddressWarning>
            )}
        </AddressContentWrapper>
    );
});
